import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Phone, Shield, MapPin, Edit, Trash2, Check, X, KeyRound,
  Loader2, BellRing, StickyNote, Clock, Briefcase, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Textarea } from '../ui/textarea';
import { SkeletonCards } from '../Brand/BrandLoading';
import EmptyState from '../ui/empty-state';
import { ukDate } from '../../lib/format';
import RatingBadge from './RatingBadge';
import StaffRatingsDialog from './StaffRatingsDialog';
import useStaffScores from '../../lib/useStaffScores';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  deactivated: 'bg-cream text-slate-700',
  rejected: 'bg-red-100 text-red-700',
};

const FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'deactivated', label: 'Deactivated' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'missing_postcode', label: 'Missing home postcode' },
];

const formatLastLogin = (value) => {
  if (!value) return 'Never signed in';
  try {
    return `Last login ${new Date(value).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })}`;
  } catch (err) {
    return 'Last login unknown';
  }
};

export const StaffAccountsPanel = ({ token, onEdit, onDelete, refreshKey }) => {
  const [data, setData] = useState({ staff: [], counts: {} });
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [notesFor, setNotesFor] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [ratingsFor, setRatingsFor] = useState(null);
  const [scoresKey, setScoresKey] = useState(0);
  const scores = useStaffScores(token, scoresKey);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/staff-accounts`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load staff accounts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (label, request) => {
    setBusy(label);
    try {
      const res = await request();
      toast.success(res?.data?.message || 'Done');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'That did not work');
    } finally {
      setBusy(null);
    }
  };

  const saveNotes = async (member) => {
    await act(`notes-${member.id}`, () =>
      axios.put(`${API}/admin/staff/${member.id}/notes`, { admin_notes: notesDraft }, { headers })
    );
    setNotesFor(null);
  };

  const nudgeAll = async () => {
    setBusy('nudge');
    try {
      const res = await axios.post(`${API}/admin/staff/nudge-postcode`, {}, { headers });
      if (res.data.nudged_count === 0) {
        toast.success('Everyone has a home postcode already');
      } else {
        toast.success(
          `Nudged ${res.data.nudged_count} staff` +
          (res.data.email_sent ? '' : ' (in-app only - email is not connected yet)')
        );
      }
    } catch (err) {
      toast.error('Could not send the nudge');
    } finally {
      setBusy(null);
    }
  };

  const visible = (data.staff || []).filter((member) => {
    if (filter === 'missing_postcode') return member.missing_home_postcode && member.account_status === 'active';
    return member.account_status === filter;
  });

  const counts = data.counts || {};

  if (loading) {
    return <SkeletonCards count={3} testId="staff-loading" />;
  }

  return (
    <div className="space-y-4" data-testid="staff-accounts-panel">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const count = f.key === 'missing_postcode' ? counts.missing_home_postcode : counts[f.key];
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`staff-filter-${f.key}`}
              className={`min-h-9 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                filter === f.key
                  ? 'bg-gold text-ink border-gold'
                  : 'bg-white text-slate-600 border-hair hover:border-gold hover:bg-cream'
              }`}
            >
              {f.label} ({count || 0})
            </button>
          );
        })}

        {filter === 'missing_postcode' && (
          <Button
            size="sm"
            onClick={nudgeAll}
            disabled={busy === 'nudge'}
            className="ml-auto bg-amber-500 hover:bg-amber-600"
            data-testid="nudge-postcode-btn"
          >
            {busy === 'nudge' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />}
            Nudge everyone missing a postcode
          </Button>
        )}
      </div>

      {counts.pending > 0 && filter !== 'pending' && (
        <button
          onClick={() => setFilter('pending')}
          className="w-full text-left rounded-[10px] border border-gold bg-cream px-4 py-3 text-sm text-ink transition-colors hover:bg-gold-soft"
          data-testid="pending-applications-banner"
        >
          <strong>{counts.pending} new application{counts.pending > 1 ? 's' : ''}</strong> waiting for approval — review now
        </button>
      )}

      {visible.length === 0 && (
        <EmptyState
          icon={Shield}
          title="Nobody here"
          line="No staff match this list yet."
          testId="staff-empty-state"
        />
      )}

      <div className="grid gap-4">
        {visible.map((member) => (
          <Card key={member.id} className="bg-white transition-shadow hover:shadow-gold" data-testid={`staff-card-${member.email}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Avatar className="mt-1">
                    {member.profile_photo && <AvatarImage src={member.profile_photo} alt={member.name} />}
                    <AvatarFallback>{(member.name || '?').charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-ink">{member.name}</h3>
                      <Badge className={`text-xs ${STATUS_STYLES[member.account_status] || ''}`} data-testid={`staff-status-${member.email}`}>
                        {member.account_status}
                      </Badge>
                      {member.missing_home_postcode && (
                        <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">no postcode</Badge>
                      )}
                      {member.can_drive !== null && member.can_drive !== undefined && (
                        <Badge variant="outline" className={`text-xs ${member.can_drive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {member.can_drive ? 'Can drive' : 'No driving'}
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-1 text-sm text-slate-600">
                      <RatingBadge
                        score={scores[member.email]?.score}
                        reliability={scores[member.email]?.reliability}
                        testId={`staff-rating-${member.email}`}
                      />
                      <div className="flex items-center gap-2"><Mail className="w-3 h-3" /><span className="truncate">{member.email}</span></div>
                      {member.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" /><span>{member.phone}</span></div>}
                      {member.home_postcode && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /><span>{member.home_postcode}</span></div>}
                      {member.emergency_contact && <div className="flex items-center gap-2"><Shield className="w-3 h-3" /><span>Emergency: {member.emergency_contact}</span></div>}
                      <div className="flex items-center gap-2 text-slate-400">
                        <Clock className="w-3 h-3" />
                        <span data-testid={`staff-last-login-${member.email}`}>{formatLastLogin(member.last_login)}</span>
                      </div>
                    </div>

                    {member.services_provided?.length > 0 && (
                      <div className="flex items-start gap-2 mt-2">
                        <Briefcase className="w-3 h-3 mt-1 text-slate-400" />
                        <div className="flex flex-wrap gap-1">
                          {member.services_provided.map((service, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{service}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 justify-end">
                  {member.account_status === 'pending' && (
                    <>
                      <Button
                        size="sm" className="bg-green-600 hover:bg-green-700"
                        disabled={busy === `approve-${member.id}`}
                        onClick={() => act(`approve-${member.id}`, () => axios.post(`${API}/admin/staff/${member.id}/approve`, {}, { headers }))}
                        data-testid={`approve-staff-${member.email}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                        disabled={busy === `reject-${member.id}`}
                        onClick={() => act(`reject-${member.id}`, () => axios.post(`${API}/admin/staff/${member.id}/reject`, {}, { headers }))}
                        data-testid={`reject-staff-${member.email}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}

                  {member.account_status === 'active' && (
                    <Button
                      size="sm" variant="outline" className="text-slate-600"
                      disabled={busy === `deactivate-${member.id}`}
                      onClick={() => act(`deactivate-${member.id}`, () => axios.put(`${API}/admin/staff/${member.id}/deactivate`, {}, { headers }))}
                      data-testid={`deactivate-staff-${member.email}`}
                    >
                      Deactivate
                    </Button>
                  )}

                  {(member.account_status === 'deactivated' || member.account_status === 'rejected') && (
                    <Button
                      size="sm" variant="outline" className="text-green-700 border-green-200 hover:bg-green-50"
                      disabled={busy === `reactivate-${member.id}`}
                      onClick={() => act(`reactivate-${member.id}`, () => axios.put(`${API}/admin/staff/${member.id}/reactivate`, {}, { headers }))}
                      data-testid={`reactivate-staff-${member.email}`}
                    >
                      Reactivate
                    </Button>
                  )}

                  <Button
                    size="sm" variant="outline" className="text-amber-700 border-amber-200 hover:bg-amber-50"
                    disabled={busy === `reset-${member.id}`}
                    onClick={() => act(`reset-${member.id}`, () => axios.post(`${API}/admin/staff/${member.id}/send-password-reset`, {}, { headers }))}
                    data-testid={`send-reset-${member.email}`}
                  >
                    <KeyRound className="w-4 h-4 mr-1" /> Send password reset
                  </Button>

                  <Button
                    size="sm" variant="outline"
                    onClick={() => setRatingsFor(member)}
                    data-testid={`ratings-open-${member.email}`}
                  >
                    <Star className="w-4 h-4 mr-1" /> Ratings
                  </Button>

                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setNotesFor(notesFor === member.id ? null : member.id); setNotesDraft(member.admin_notes || ''); }}
                    data-testid={`notes-toggle-${member.email}`}
                  >
                    <StickyNote className="w-4 h-4 mr-1" /> Notes
                  </Button>

                  <Button size="sm" variant="outline" onClick={() => onEdit(member)} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid={`edit-staff-${member.email}`}>
                    <Edit className="w-4 h-4" />
                  </Button>

                  <Button size="sm" variant="outline" onClick={() => onDelete(member.id)} className="text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {member.admin_notes && notesFor !== member.id && (
                <div className="rounded-[10px] bg-cream border border-hair p-3 text-sm text-slate-700" data-testid={`staff-notes-${member.email}`}>
                  <p className="text-xs font-semibold text-slate-500 mb-1">Private notes (staff never see these)</p>
                  <p className="whitespace-pre-wrap">{member.admin_notes}</p>
                </div>
              )}

              {notesFor === member.id && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500">Private notes (staff never see these)</p>
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={3}
                    placeholder="Anything you want to remember about this staff member…"
                    data-testid={`notes-input-${member.email}`}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveNotes(member)} className="bg-slate-900 hover:bg-slate-800" data-testid={`notes-save-${member.email}`}>
                      Save notes
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setNotesFor(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <StaffRatingsDialog
        token={token}
        member={ratingsFor}
        open={!!ratingsFor}
        onClose={() => setRatingsFor(null)}
        onSaved={() => setScoresKey((key) => key + 1)}
      />
    </div>
  );
};

export default StaffAccountsPanel;
